import { canDelete, canEdit as canEditArea, canEditInvoices } from '../lib/area';
import { DeleteButton } from '../components/DeleteButton';
import { api } from '../lib/http';
import { useRememberedFilter } from '../lib/remember';
import { getAreaName } from '../lib/area';
import { quarterOf, type ForecastGap, type InvoiceItem, type Lookups, type Me, type OrderListItem } from '@ft/shared';
import type { T } from '../i18n';
import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ExportButton } from '../components/ExportButton';
import { SavedViews } from '../components/SavedViews';
import { useNewShortcut } from '../components/CommandPalette';
import { InvoiceForm } from '../components/InvoiceForm';
import { WorkdaySyncBox } from '../components/WorkdaySyncBox';
import { Cust, Loading, useLoad, useToast } from '../components/ui';
import { pct, dfmt, eur, eur2 } from '../lib/format';
import { useI18n } from '../i18n';
import { Chip, CollapseAll, Panel } from '../lib/collapse';

type PayState = 'paid' | 'unpaid' | 'overdue' | 'unknown' | 'warranty';
const payState = (i: InvoiceItem): PayState => i.warranty ? 'warranty' : !i.payment ? 'unknown' : i.payment.status === 'paid' ? 'paid' : i.payment.overdueDays ? 'overdue' : 'unpaid';
const PAY_TONE: Record<PayState, string> = { paid: 'pos', unpaid: 'info', overdue: 'neg', unknown: 'neu', warranty: 'neu' };

/** Zahlungsstatus laut Workday als Pill mit Alter (überfällig seit / offen seit). */
export function PaymentPill({ i, t }: { i: InvoiceItem; t: T }) {
  const st = payState(i);
  const p = i.payment;
  if (st === 'warranty') return <span className="pill neu wty" title={t('pay.warrantyHint')}>{t('pay.warranty')}</span>;
  const title = p ? [p.status === 'paid' && p.paidAt ? t('pay.paidOn', { date: dfmt(p.paidAt) }) : '', p.dueDate ? t('pay.dueOn', { date: dfmt(p.dueDate) }) : '', p.openDays !== null ? t('pay.openDays', { n: p.openDays }) : '', p.terms ?? '', p.source === 'manual' ? t('pay.manual') : ''].filter(Boolean).join(' · ') : undefined;
  return (
    <span title={title}>
      <span className={`pill ${PAY_TONE[st]}`}>{st === 'overdue' ? t('pay.overdueDays', { n: p!.overdueDays! }) : t(`pay.${st}`)}{p?.source === 'manual' && <span aria-hidden="true" title={t('pay.manual')}> ✎</span>}</span>
      {st === 'paid' && p?.paidAt && <div className="muted small num">{dfmt(p.paidAt)}</div>}
      {st === 'overdue' && p && p.openNetCents !== i.amountCents && <div className="muted small num">{t('pay.openNet', { v: eur2(p.openNetCents) })}</div>}
    </span>
  );
}

/** Kennzahl-Kachel: Etikett einzeilig oben, Zahl auf fester Höhe, Zusatzzeile darunter. */
function Kpi({ label, value, sub, tone, children, className }: { label: string; value: string; sub?: string; tone?: 'pos' | 'neg'; children?: React.ReactNode; className?: string }) {
  return (
    <div className={`ikpi${className ? ` ${className}` : ''}`}>
      <div className="l" title={label}>{label}</div>
      <div className={`v num${tone ? ` ${tone}` : ''}`}>{value}</div>
      {sub && <div className="s" title={sub}>{sub}</div>}
      {children}
    </div>
  );
}

/** Erklärung als ⓘ mit Tooltip statt Fließtext in der Kopfzeile. */
const Info = ({ text }: { text: string }) => <i className="infoi" title={text} aria-label={text} role="img">i</i>;

/** Zahlungsstand: bezahlt (netto), offen, davon überfällig; darunter offen nach Tagen seit Fälligkeit. Filter der Liste gelten mit. */
function OpenItems({ list, onOverdue, t }: { list: InvoiceItem[]; onOverdue: () => void; t: T }) {
  const open = list.filter((i) => i.payment?.status === 'unpaid');
  const paid = list.filter((i) => i.payment?.status === 'paid');
  const paidCents = paid.reduce((s, i) => s + i.amountCents, 0);
  const billedCents = list.reduce((s, i) => s + i.amountCents, 0);
  const unknown = list.filter((i) => !i.payment).length;
  if (!open.length && list.every((i) => !i.payment)) return null;
  const due = (i: InvoiceItem) => i.payment?.openNetCents ?? i.amountCents;
  const sum = (xs: InvoiceItem[]) => xs.reduce((s, i) => s + due(i), 0);
  const over = open.filter((i) => i.payment?.overdueDays);
  const oldest = over.reduce((m, i) => Math.max(m, i.payment!.overdueDays!), 0);
  const share = billedCents > 0 ? Math.max(0, Math.min(1, paidCents / billedCents)) : 0;
  const buckets: { k: string; xs: InvoiceItem[]; late: boolean }[] = [
    { k: 'pay.age.notDue', xs: open.filter((i) => !i.payment?.overdueDays), late: false },
    { k: 'pay.age.d30', xs: over.filter((i) => i.payment!.overdueDays! <= 30), late: true },
    { k: 'pay.age.d60', xs: over.filter((i) => i.payment!.overdueDays! > 30 && i.payment!.overdueDays! <= 60), late: true },
    { k: 'pay.age.d90', xs: over.filter((i) => i.payment!.overdueDays! > 60 && i.payment!.overdueDays! <= 90), late: true },
    { k: 'pay.age.more', xs: over.filter((i) => i.payment!.overdueDays! > 90), late: true },
  ];
  return (
    <Panel id="inv.pay" title={t('pay.boxTitle')} hint={<Info text={t('pay.boxHint')} />}
      summary={<>
        <Chip tone="pos">{t('pay.kpiPaid')} <b>{eur(paidCents)}</b></Chip>
        <Chip>{t('pay.kpiUnpaid')} <b>{eur(sum(open))}</b></Chip>
        {over.length > 0 && <Chip tone="neg">{t('pay.kpiOverdue')} <b>{eur(sum(over))}</b></Chip>}
      </>}
      actions={over.length > 0 ? <button className="btn ghost" onClick={onOverdue}>{t('pay.showOverdue')}</button> : undefined}>
      <div className="ikpis k3">
        <Kpi label={t('pay.kpiPaid')} value={eur(paidCents)} tone={paid.length ? 'pos' : undefined}
          sub={`${t('pay.kpiCount', { n: paid.length })}${billedCents > 0 ? ` · ${t('pay.kpiPaidShare', { p: pct(share) })}` : ''}`}>
          {billedCents > 0 && <div className="imeter" role="img" aria-label={pct(share)}><i style={{ width: `${share * 100}%` }} /></div>}
        </Kpi>
        <Kpi label={t('pay.kpiUnpaid')} value={eur(sum(open))} sub={t('pay.kpiCount', { n: open.length })} />
        <Kpi label={t('pay.kpiOverdue')} value={eur(sum(over))} tone={over.length ? 'neg' : undefined}
          sub={`${t('pay.kpiCount', { n: over.length })}${oldest ? ` · ${t('pay.oldest', { n: oldest })}` : ''}`} />
      </div>
      {open.length > 0 && (
        <div className="iaging">
          <span className="t">{t('pay.agingTitle')}</span>
          {buckets.map((b) => (
            <span key={b.k} className={`seg${!b.xs.length ? ' zero' : b.late ? ' hit' : ''}`}>
              {t(b.k as 'pay.age.d30')} <b className="num">{eur(sum(b.xs))}</b>{b.xs.length > 0 && <span className="c">· {b.xs.length}</span>}
            </span>
          ))}
        </div>
      )}
      {unknown > 0 && <p className="muted small" style={{ padding: '0 18px 12px', margin: 0 }}>{t('pay.unknownHint', { n: unknown })}</p>}
    </Panel>
  );
}

type MName = (m: number, style: 'long' | 'short') => string;
/** Zeitraum, für den der Forecast zählt: „Jan–Okt“ (laufender Monat erst ab seinem letzten Arbeitstag). */
const gapRange = (gap: ForecastGap, mName: MName, t: T) => (gap.month < 0 ? t('fg.noMonth') : gap.month === 0 ? mName(0, 'short') : `${mName(0, 'short')}–${mName(gap.month, 'short')}`);
/** Marke „Stand Oktober · November ab 30.11.“ */
function GapTag({ gap, mName, t, lang }: { gap: ForecastGap; mName: MName; t: T; lang: string }) {
  const next = gap.nextMonth !== null && gap.nextFrom ? t('fg.nextFrom', { m: mName(gap.nextMonth, 'long'), d: new Date(`${gap.nextFrom}T00:00:00Z`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'de-DE', { day: '2-digit', month: '2-digit', timeZone: 'UTC' }) }) : '';
  return <span className="itag">{gap.month >= 0 ? t('fg.asOf', { m: mName(gap.month, 'long') }) : t('fg.noMonth')}{next ? ` · ${next}` : ''}</span>;
}

/**
 * Ausstehend: was noch reinkommen muss = offen (Rechnungen gestellt, nicht bezahlt, netto) + noch zu fakturieren
 * (Forecast bis zum gezählten Monat minus Abgerechnet, nur positive Lücken). Zeile je Projekt; Kunden-/Projektfilter gelten mit.
 */
function OutstandingBox({ gap, invoices, customer, projectId, t, lang }: { gap: ForecastGap | null; invoices: InvoiceItem[]; customer: string; projectId: string; t: T; lang: string }) {
  const by = new Map<string, { project: string; customer: string; open: number; openCount: number; toInvoice: number }>();
  const row = (id: string, project: string, cust: string) => by.get(id) ?? (by.set(id, { project, customer: cust, open: 0, openCount: 0, toInvoice: 0 }), by.get(id)!);
  for (const i of invoices) {
    if (i.warranty || i.payment?.status !== 'unpaid') continue;
    if ((customer && i.customer.name !== customer) || (projectId && i.project.id !== projectId)) continue;
    const r = row(i.project.id, i.project.name, i.customer.name);
    r.open += i.payment.openNetCents; r.openCount++;
  }
  for (const p of gap?.projects ?? []) {
    if ((customer && p.customer !== customer) || (projectId && p.projectId !== projectId) || p.gapCents <= 0) continue;
    row(p.projectId, p.project, p.customer).toInvoice += p.gapCents;
  }
  const rows = [...by.values()].filter((r) => r.open + r.toInvoice > 0).sort((a, b) => (b.open + b.toInvoice) - (a.open + a.toInvoice));
  if (!rows.length) return null;
  const open = rows.reduce((s, r) => s + r.open, 0), toInvoice = rows.reduce((s, r) => s + r.toInvoice, 0), count = rows.reduce((s, r) => s + r.openCount, 0);
  const loc = lang === 'en' ? 'en-GB' : 'de-DE';
  const mName: MName = (m, style) => new Date(Date.UTC(gap?.year ?? 2000, m, 1)).toLocaleDateString(loc, { month: style, timeZone: 'UTC' }).replace('.', '');
  return (
    <Panel id="inv.out" title={t('out.title')} hint={<>{gap && <GapTag gap={gap} mName={mName} t={t} lang={lang} />}<Info text={t('out.hint')} /></>}
      summary={<>
        <Chip>{t('out.open')} <b>{eur(open)}</b></Chip>
        <Chip>{t('out.toInvoice')} <b>{eur(toInvoice)}</b></Chip>
        <Chip tone="info">{t('out.total')} <b>{eur(open + toInvoice)}</b></Chip>
      </>}>
      <div className="icalc icalc3">
        <Kpi label={t('out.open')} value={eur(open)} sub={t('out.openSub', { n: count })} />
        <div className="op" aria-hidden="true">+</div>
        <Kpi label={t('out.toInvoice')} value={eur(toInvoice)} sub={gap && gap.month >= 0 ? t('out.toInvoiceSub', { r: gapRange(gap, mName, t) }) : t('fg.gapNone')} />
        <div className="op" aria-hidden="true">=</div>
        <Kpi className="res info" label={t('out.total')} value={eur(open + toInvoice)} sub={t('out.totalSub')} />
      </div>
      {rows.length > 1 && (
        <div className="tablewrap" style={{ borderTop: '1px solid var(--line)' }}>
          <table>
            <thead><tr><th>{t('common.project')}</th><th>{t('common.customer')}</th><th className="r">{t('out.col.open')}</th><th className="r">{t('out.col.toInvoice')}</th><th className="r">{t('out.col.total')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.project + r.customer}>
                  <td>{r.project}</td><td>{r.customer}</td>
                  <td className="r num">{r.open ? eur(r.open) : <span className="muted">–</span>}</td>
                  <td className="r num">{r.toInvoice ? eur(r.toInvoice) : <span className="muted">–</span>}</td>
                  <td className="r num" style={{ fontWeight: 600 }}>{eur(r.open + r.toInvoice)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr><td colSpan={2}><b>{t('out.sum')}</b></td><td className="r num"><b>{eur(open)}</b></td><td className="r num"><b>{eur(toInvoice)}</b></td><td className="r num"><b>{eur(open + toInvoice)}</b></td></tr></tfoot>
          </table>
        </div>
      )}
    </Panel>
  );
}

/**
 * Forecast gegen Rechnungen als Rechenweg: Forecast Januar bis einschließlich laufenden Monat (springt jeden Monat weiter)
 * − Rechnungen des Jahres = noch zu fakturieren; daneben Gesamtjahr und Rest. Kunden-/Projektfilter gelten mit.
 */
function ForecastGapBox({ gap, invoices, customer, projectId, t, lang }: { gap: ForecastGap; invoices: InvoiceItem[]; customer: string; projectId: string; t: T; lang: string }) {
  const rows = gap.projects.filter((p) => (!customer || p.customer === customer) && (!projectId || p.projectId === projectId));
  if (!rows.length) return null;
  const sum = (k: 'forecastToDateCents' | 'billedCents' | 'gapCents' | 'forecastYearCents' | 'restYearCents') => rows.reduce((s, p) => s + p[k], 0);
  const loc = lang === 'en' ? 'en-GB' : 'de-DE';
  const mName = (m: number, style: 'long' | 'short') => new Date(Date.UTC(gap.year, m, 1)).toLocaleDateString(loc, { month: style, timeZone: 'UTC' }).replace('.', '');
  const range = gapRange(gap, mName, t);
  const ids = new Set(rows.map((p) => p.projectId));
  const count = invoices.filter((i) => ids.has(i.project.id) && i.invoiceDate.startsWith(String(gap.year))).length;
  const g = sum('gapCents');
  return (
    <Panel id="inv.gap" title={t('fg.title', { y: gap.year })} hint={<><GapTag gap={gap} mName={mName} t={t} lang={lang} /><Info text={t('fg.hint')} /></>}
      summary={<>
        <Chip>{t('fg.forecastToDate', { r: range })} <b>{eur(sum('forecastToDateCents'))}</b></Chip>
        <Chip>{t('fg.billed', { y: gap.year })} <b>{eur(sum('billedCents'))}</b></Chip>
        <Chip tone={g > 0 ? 'crit' : 'pos'}>{g > 0 ? t('fg.gap') : t('fg.ahead')} <b>{eur(Math.abs(g))}</b></Chip>
      </>}>
      <div className="icalc">
        <Kpi label={t('fg.forecastToDate', { r: range })} value={eur(sum('forecastToDateCents'))} sub={t('fg.fromFile')} />
        <div className="op" aria-hidden="true">−</div>
        <Kpi label={t('fg.billed', { y: gap.year })} value={eur(sum('billedCents'))} sub={t('pay.kpiCount', { n: count })} />
        <div className="op" aria-hidden="true">=</div>
        <Kpi className={g > 0 ? 'res neg' : 'res pos'} label={g > 0 ? t('fg.gap') : t('fg.ahead')} value={eur(Math.abs(g))} tone={g > 0 ? 'neg' : 'pos'}
          sub={g > 0 ? (gap.month >= 0 ? t('fg.gapSub', { m: mName(gap.month, 'long') }) : t('fg.gapNone')) : t('fg.aheadSub')} />
        <div className="sep" aria-hidden="true" />
        <Kpi label={t('fg.year')} value={eur(sum('forecastYearCents'))} sub={`${mName(0, 'short')}–${mName(11, 'short')}`} />
        <Kpi label={t('fg.rest')} value={eur(Math.max(0, sum('restYearCents')))} sub={t('fg.restSub')} />
      </div>
      {rows.length > 1 && (
        <div className="tablewrap" style={{ borderTop: '1px solid var(--line)' }}>
          <table>
            <thead><tr><th>{t('common.project')}</th><th>{t('common.customer')}</th><th className="r">{t('fg.col.forecast', { r: range })}</th><th className="r">{t('fg.col.billed')}</th><th className="r">{t('fg.col.gap')}</th><th className="r">{t('fg.col.rest')}</th></tr></thead>
            <tbody>
              {[...rows].sort((a, b) => b.gapCents - a.gapCents).map((p) => (
                <tr key={p.projectId}>
                  <td>{p.project}</td><td>{p.customer}</td>
                  <td className="r num">{eur(p.forecastToDateCents)}</td><td className="r num">{eur(p.billedCents)}</td>
                  <td className="r num" style={p.gapCents > 0 ? { color: 'var(--neg)', fontWeight: 600 } : { color: 'var(--muted)' }}>{p.gapCents > 0 ? eur(p.gapCents) : '–'}</td>
                  <td className="r num">{eur(Math.max(0, p.restYearCents))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}

export function InvoicesPage({ me }: { me: Me }) {
  const { t, lang } = useI18n();
  const [sp, setSp] = useSearchParams();
  useRememberedFilter('invoicing');
  const { data, error, reload } = useLoad<InvoiceItem[]>('/api/invoices');
  const { data: gap, reload: reloadGap } = useLoad<ForecastGap>('/api/invoices/forecast-gap');
  const { data: lookups } = useLoad<Lookups>('/api/lookups');
  const { data: orders, reload: reloadOrders } = useLoad<OrderListItem[]>('/api/orders');
  const [form, setForm] = useState<{ invoice: InvoiceItem | null } | null>(null);
  const [toast, say] = useToast();
  // Anlegen: PM/Admin; bearbeiten (Zahlung, Warranty) und Workday-Abgleich: auch Finance
  const canCreate = canEditArea(me);
  const canEdit = canEditInvoices(me);
  useNewShortcut(useCallback(() => setForm({ invoice: null }), []), canCreate);
  const ready = !!lookups && !!orders;
  const projRef = (id: string) => lookups?.projects.find((p) => p.id === id);
  const canDel = (i: InvoiceItem) => { const p = projRef(i.project.id); return !!p && canDelete(me, [p]); };
  const anyDel = !!data?.some(canDel);

  const f = { q: sp.get('q') ?? '', c: sp.get('kunde') ?? '', p: sp.get('projekt') ?? '', quarter: sp.get('quartal') ?? '', noOrder: sp.get('ohne') === '1', pay: sp.get('zahlung') ?? '' };
  const set = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const years = [...new Set(data?.map((i) => i.invoiceDate.slice(0, 4)) ?? [])].sort().reverse();

  const list = useMemo(() => {
    if (!data) return [];
    const q = f.q.toLowerCase();
    return data.filter((i) =>
      (!f.c || i.customer.name === f.c) &&
      (!f.p || i.project.id === f.p) &&
      (!f.quarter || `${i.invoiceDate.slice(0, 4)}-Q${quarterOf(i.invoiceDate)}` === f.quarter || i.invoiceDate.slice(0, 4) === f.quarter) &&
      (!f.noOrder || !i.order) &&
      (!f.pay || (f.pay === 'unpaid' ? !i.warranty && i.payment?.status === 'unpaid' : payState(i) === f.pay)) &&
      (!q || [i.invoiceNumber, i.project.name, i.customer.name, i.order?.orderNumber].join(' ').toLowerCase().includes(q)));
  }, [data, f.q, f.c, f.p, f.quarter, f.noOrder, f.pay]);

  const projects = lookups?.projects.filter((p) => !f.c || lookups.customers.find((c) => c.id === p.customerId)?.name === f.c) ?? [];
  const total = list.reduce((s, i) => s + i.amountCents, 0);

  return (
    <section className="page">
      <div className="pagehead">
        <div><div className="crumb">{t('crumb.root', { area: getAreaName() })}</div><h1>{t('inv.title')}</h1></div>
        <span className="spacer" />
        <CollapseAll ids={['inv.pay', 'inv.out', 'inv.gap']} />
        <SavedViews />
        <ExportButton />
        {canEdit && <Link className="btn" to="/invoicing/workday">{t('wd.button')}</Link>}
        {canCreate && <button className="btn primary" onClick={() => setForm({ invoice: null })} disabled={!ready}>{t('inv.add')}</button>}
      </div>

      <WorkdaySyncBox canEdit={canEdit} onSynced={() => { reload(); reloadOrders(); reloadGap(); }} />

      <div className="fbar">
        <div className="fld"><label htmlFor="iq">{t('common.search')}</label><input id="iq" type="search" value={f.q} onChange={(e) => set('q', e.target.value)} placeholder={t('inv.searchPh')} /></div>
        <div className="fld"><label htmlFor="ic">{t('common.customer')}</label><select id="ic" value={f.c} onChange={(e) => { set('kunde', e.target.value); }}><option value="">{t('common.all')}</option>{lookups?.customers.map((c) => <option key={c.id}>{c.name}</option>)}</select></div>
        <div className="fld"><label htmlFor="ip">{t('common.project')}</label><select id="ip" value={f.p} onChange={(e) => set('projekt', e.target.value)}><option value="">{t('common.all')}</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
        <div className="fld"><label htmlFor="iqt">{t('inv.period')}</label><select id="iqt" value={f.quarter} onChange={(e) => set('quartal', e.target.value)}><option value="">{t('common.all')}</option>{years.map((y) => [<option key={y} value={y}>{y}</option>, ...[1, 2, 3, 4].map((q) => <option key={`${y}-${q}`} value={`${y}-Q${q}`}>{y} · Q{q}</option>)])}</select></div>
        <div className="fld"><label htmlFor="io">{t('inv.order')}</label><select id="io" value={f.noOrder ? '1' : ''} onChange={(e) => set('ohne', e.target.value)}><option value="">{t('common.all')}</option><option value="1">{t('inv.withoutOrder')}</option></select></div>
        <div className="fld"><label htmlFor="ipay">{t('pay.filter')}</label><select id="ipay" value={f.pay} onChange={(e) => set('zahlung', e.target.value)}><option value="">{t('common.all')}</option>{(['unpaid', 'overdue', 'paid', 'unknown', 'warranty'] as const).map((k) => <option key={k} value={k}>{t(`pay.${k}`)}</option>)}</select></div>
        {Object.values(f).some(Boolean) && <button className="btn ghost" onClick={() => setSp(new URLSearchParams(), { replace: true })}>{t('common.reset')}</button>}
      </div>

      {data && <OpenItems list={data.filter((i) => !i.warranty && (!f.c || i.customer.name === f.c) && (!f.p || i.project.id === f.p))} onOverdue={() => set('zahlung', 'overdue')} t={t} />}
      {data && <OutstandingBox gap={gap} invoices={data} customer={f.c} projectId={f.p} t={t} lang={lang} />}
      {gap && data && <ForecastGapBox gap={gap} invoices={data} customer={f.c} projectId={f.p} t={t} lang={lang} />}

      {!data ? <Loading error={error} /> : (
        <div className="box tablewrap">
          {!list.length ? <div className="empty">{t('inv.empty')}</div> : (
            <table>
              <thead><tr><th>{t('inv.col.number')}</th><th>{t('common.date')}</th><th>{t('common.quarter')}</th><th>{t('common.customer')}</th><th>{t('common.project')}</th><th>{t('inv.order')}</th><th>{t('pay.col')}</th><th className="r">{t('common.amount')}</th>{anyDel && <th aria-label={t('common.archive')} />}</tr></thead>
              <tbody>
                {list.map((i) => (
                  <tr key={i.id} className={canEdit ? 'click' : undefined} tabIndex={canEdit ? 0 : undefined} onClick={() => canEdit && ready && setForm({ invoice: i })} onKeyDown={(e) => { if (e.key === 'Enter' && canEdit && ready) setForm({ invoice: i }); }}>
                    <td className="mono">{i.invoiceNumber}</td>
                    <td className="num">{dfmt(i.invoiceDate)}</td>
                    <td>Q{quarterOf(i.invoiceDate)}</td>
                    <td><Cust name={i.customer.name} /></td>
                    <td>{i.project.name}</td>
                    <td>{i.order ? <Link className="idlink" to={`/bestellungen/${i.order.id}`} onClick={(e) => e.stopPropagation()}>{i.order.orderNumber ?? t('common.noNumber')}</Link> : <span className="pill neu">{t('inv.noOrder')}</span>}</td>
                    <td className="nowrap"><PaymentPill i={i} t={t} /></td>
                    <td className="r num">{eur2(i.amountCents)}</td>
                    {anyDel && <td className="delcell">{canDel(i) && <DeleteButton compact what={i.invoiceNumber} onDelete={async () => { try { await api('DELETE', `/api/invoices/${i.id}?version=${i.version}`); reload(); reloadOrders(); say(t('del.done')); } catch (e) { say((e as Error).message); } }} />}</td>}
                  </tr>
                ))}
              </tbody>
              <tfoot><tr><td colSpan={7}>{t('inv.count', { n: list.length })}</td><td className="r num">{eur(total)}</td>{anyDel && <td />}</tr></tfoot>
            </table>
          )}
        </div>
      )}

      {form && lookups && orders && (
        <InvoiceForm invoice={form.invoice} lookups={lookups} orders={orders} canDelete={!!form.invoice && canDel(form.invoice)} paymentOnly={!canCreate}
          preset={f.p ? { projectId: f.p } : undefined}
          onClose={() => setForm(null)} onSaved={() => { setForm(null); reload(); reloadOrders(); reloadGap(); say(t('invoice.saved')); }} />
      )}
      {toast}
    </section>
  );
}
